import { Injectable } from '@angular/core';

/**
 * The ceiling above which a TIFF is not decoded in the tab.
 *
 * A decoded TIFF costs 4 bytes per pixel as RGBA, and the canvas it is painted onto holds a
 * second copy - so the peak is ~8 bytes per pixel. At 32 megapixels that is ~256 MB, which a
 * desktop tab survives; a 100 MP archival scan would be ~800 MB and freezes it. Above the
 * ceiling the panel shows the server thumbnail and the download button instead, which is a
 * worse view of the file and an enormously better one than a hung browser.
 *
 * 32 MP is also comfortably inside Chrome's canvas area limit (~268 MP) rather than resting
 * on it, and above every image in the rig corpus.
 */
export const TIFF_MAX_PIXELS = 32_000_000;

/**
 * Browsers refuse a canvas wider or taller than this regardless of its area, so a long thin
 * panorama has to be caught on the dimension as well as on the pixel count.
 */
export const TIFF_MAX_DIMENSION = 16_384;

export type TiffRefusal = 'too-large' | 'unsupported';

export type TiffDecodeResult =
  | { ok: true; blob: Blob; width: number; height: number }
  | { ok: false; reason: TiffRefusal; width: number; height: number };

/**
 * Whether a TIFF of these dimensions may be decoded in the tab.
 *
 * Separate from the decode so it can be answered from the IFD header alone - UTIF.decode parses
 * the directories without touching the pixels, so an image over the ceiling is refused before a
 * single byte of it is expanded.
 */
export function tiffFitsInBrowser(p_width: number, p_height: number): boolean {
  if (!(p_width > 0) || !(p_height > 0)) { return false; }
  if (p_width > TIFF_MAX_DIMENSION || p_height > TIFF_MAX_DIMENSION) { return false; }
  return p_width * p_height <= TIFF_MAX_PIXELS;
}

/**
 * Renders a TIFF to a PNG blob the browser can display.
 *
 * No browser but Safari decodes TIFF, so a TIFF blobbed straight into an <img> is a broken
 * image icon. Decoding a format the browser cannot render natively is already how this app
 * shows PDFs (pdf.js) and deep-zoom images (openseadragon); this is the same move for TIFF.
 *
 * The refusals are explicit and typed, because the caller must say WHICH one happened. A blank
 * frame is not an answer, and neither is a broken <img>.
 */
@Injectable({ providedIn: 'root' })
export class TiffPreviewService {
  private utif: any = null;

  /** Loaded on first use, so a record with no TIFF never pays for the decoder. */
  private async loadUtif(): Promise<any> {
    if (!this.utif) {
      const mod: any = await import('utif');
      this.utif = mod?.default ?? mod;
    }
    return this.utif;
  }

  async toPng(p_bytes: ArrayBuffer): Promise<TiffDecodeResult> {
    let width = 0;
    let height = 0;

    try {
      const utif = await this.loadUtif();

      const ifds = utif.decode(p_bytes);
      if (!ifds || ifds.length === 0) {
        return { ok: false, reason: 'unsupported', width: 0, height: 0 };
      }

      const ifd = ifds[0];
      width = Number(ifd.width ?? ifd.t256?.[0] ?? 0);
      height = Number(ifd.height ?? ifd.t257?.[0] ?? 0);

      // A truncated or malformed TIFF parses to an IFD with no dimensions. That is a file we
      // cannot read, not a file that is too big, and saying "too large" about it would send the
      // user looking for a problem they do not have.
      if (!(width > 0) || !(height > 0)) {
        return { ok: false, reason: 'unsupported', width: 0, height: 0 };
      }

      if (!tiffFitsInBrowser(width, height)) {
        return { ok: false, reason: 'too-large', width, height };
      }

      // toRGBA8 is where an unsupported photometric or sample format gives up: CMYK/Separated,
      // 16-bit and float samples, YCbCr subsampling and JPEG2000-in-TIFF are the known ones.
      utif.decodeImage(p_bytes, ifd, ifds);
      const rgba = utif.toRGBA8(ifd);
      if (!rgba || rgba.length < width * height * 4) {
        return { ok: false, reason: 'unsupported', width, height };
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return { ok: false, reason: 'unsupported', width, height };
      }
      ctx.putImageData(new ImageData(new Uint8ClampedArray(rgba.buffer, rgba.byteOffset, width * height * 4), width, height), 0, 0);

      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));

      // Release the backing store rather than waiting for the collector: two of these alive at
      // once on a large scan is the difference between a slow tab and a dead one.
      canvas.width = 0;
      canvas.height = 0;

      if (!blob) {
        return { ok: false, reason: 'unsupported', width, height };
      }

      return { ok: true, blob, width, height };
    } catch {
      return { ok: false, reason: 'unsupported', width, height };
    }
  }
}
